// @flow
import React, {useState, useEffect, useCallback} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  Hidden,
  ListItem,
  List,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import {useLocation} from 'react-router-dom';
import {colors} from '../../../../../assets/styles/colors';
import SnackBar from '../../../../../elements/SnackBar/SnackBar';
import Skeleton from '@material-ui/lab/Skeleton';
import ReviewPost from '../ReviewPost/ReviewPost';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';
import Pagination from '@material-ui/lab/Pagination';

import type {
  Pagination as PaginationType,
  Review,
} from '../../../../../entities';
import type {ReviewsAction} from '../../duck';

type Props = {
  enableReview: (payload: ReviewsAction) => void,
  fetchReviews: ({queryFilter?: {[key: string]: string}}) => void,
  isFetchingReviews?: boolean,
  pagination?: PaginationType,
  reviews?: Review[],
  enableReviewSuccess?: boolean,
  disableReviewSuccess?: boolean,
};

const useStyles = makeStyles(theme => ({
  listItem: {
    backgroundColor: 'transparent',
    padding: 0,
  },
  sizeSelect: {
    margin: theme.spacing(2, 0),
    border: 'none',
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
    },
  },
  wrapReviews: {
    background: '#F8F9FB',
    boxShadow: 0,
    borderRadius: '0px',
    [theme.breakpoints.up('md')]: {
      background: 'white',
      borderRadius: '12px',
    },
  },
  divider: {
    margin: theme.spacing(0, 3),
    background: colors.grey200,
  },
  reviewsList: {
    paddingTop: 0,
    padding: 0,
  },
  paginationArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
}));

export default function ReviewsList(props: Props) {
  const {
    enableReview,
    pagination,
    reviews,
    fetchReviews,
    isFetchingReviews,
    enableReviewSuccess,
    disableReviewSuccess,
  } = props;
  const location = useLocation();
  const classes = useStyles();

  const getSearchFromQuery = (search: string) => {
    const s = queryString.parse(search);
    return s;
  };
  const query = getSearchFromQuery(location.search);
  const p = parseInt(query.page, 10);
  const s = parseInt(query.size, 10);
  const [page, setPage] = useState(isNaN(p) ? 1 : p);
  const [size, setSize] = useState(isNaN(s) ? 16 : s);

  const buildQueryParamsCallback = useCallback(() => {
    const params: {[key: string]: any} = {
      size: size,
      page: page - 1,
    };
    const queryString = getSearchFromQuery(location.search);
    if (queryString) {
      params.name = queryString.name;
    }
    return params;
  }, [location, page, size]);

  useEffect(() => {
    const queryFilter = buildQueryParamsCallback();
    if (queryFilter) {
      fetchReviews({queryFilter});
    }
  }, [buildQueryParamsCallback, fetchReviews, location]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleSizeChange = event => {
    if (event) {
      setSize(event.target.value);
    }
  };

  const paginationArea = (
    <div className={classes.paginationArea}>
      <Pagination
        count={
          pagination &&
          (pagination.size >= pagination.total
            ? 1
            : Math.ceil(pagination.total / pagination.size))
        }
        page={page}
        className={classes.pagination}
        color="primary"
        onChange={handlePageChange}
      />
      <Hidden smUp>
        <CustomSelect
          native
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <option value={8}>8 por página</option>
          <option value={16}>16 por página</option>
          <option value={24}>24 por página</option>
          <option value={32}>32 por página</option>
        </CustomSelect>
      </Hidden>
      <Hidden xsDown>
        <CustomSelect
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <MenuItem value={8}>8 por página</MenuItem>
          <MenuItem value={16}>16 por página</MenuItem>
          <MenuItem value={24}>24 por página</MenuItem>
          <MenuItem value={32}>32 por página</MenuItem>
        </CustomSelect>
      </Hidden>
    </div>
  );

  const tablet = useMediaQuery('(max-width:960px)');

  const handleReviewEnabled = payload => {
    enableReview(payload);
  };

  return (
    <div className={classes.wrapReviews}>
      <div>
        <SnackBar
          mode="success"
          message={enableReviewSuccess ? 'Producto activado' : ''}
        />
        <SnackBar
          mode="warning"
          message={disableReviewSuccess ? 'Producto desactivado' : ''}
        />
        <List className={classes.reviewsList}>
          {isFetchingReviews && !reviews
            ? Array.from({length: size}, (x, i) => (
                <ListItem key={`skelly-${i}`}>
                  <Skeleton
                    variant="rect"
                    height={tablet ? '128px' : '48px'}
                    width="100%"
                  />
                </ListItem>
              ))
            : reviews &&
              reviews.map((reviewItem: Review, index: number) => {
                return (
                  <div key={`review-item-${index}`}>
                    <ListItem className={classes.listItem}>
                      <ReviewPost
                        review={reviewItem}
                        handleReviewEnabled={handleReviewEnabled}
                      />
                    </ListItem>
                  </div>
                );
              })}
        </List>

        {paginationArea}
      </div>
    </div>
  );
}

ReviewsList.propTypes = {
  enableReview: PropTypes.func.isRequired,
  fetchReviews: PropTypes.func.isRequired,
  pagination: PropTypes.object,
  pendingReviews: PropTypes.array,
  isFetchingReviews: PropTypes.bool,
  enableReviewSuccess: PropTypes.bool,
  disableReviewSuccess: PropTypes.bool,
};
